<template>
  <header class="sticky top-0 z-20 h-16 bg-surface border-b border-line flex items-center px-4 gap-3 flex-shrink-0">
    <!-- Toggle sidebar (mobile) -->
    <button
      class="p-1.5 rounded-lg text-muted hover:text-ink hover:bg-surfaceAlt transition-colors lg:hidden"
      @click="$store.commit('toggleSidebar')">
      <MenuIcon class="w-5 h-5" />
    </button>

    <!-- Indicadores económicos -->
    <div class="hidden md:flex items-center gap-1.5 mr-auto">
      <span
        v-for="ind in indicadoresList"
        :key="ind.key"
        class="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-surfaceAlt border border-line text-xs font-mono tabular-nums text-ink min-w-24">
        <span class="text-muted font-sans font-medium">{{ ind.label }}</span>
        <vue3-autocounter
          :startAmount="0"
          :endAmount="ind.value"
          :duration="1"
          prefix="$"
          separator="."
          decimalSeparator=","
          :decimals="2"
          :autoinit="true" />
      </span>
      <CircleQuestionMark
        v-tooltip="'Indicadores económicos diarios obtenidos desde miindicador.cl'"
        class="w-4 h-4 text-muted" />
    </div>

    <!-- Acciones -->
    <div class="flex items-center gap-1 ml-auto md:ml-0 min-w-0">
      <!-- Toggle dark/light -->
      <button
        class="p-1.5 rounded-lg text-muted hover:text-ink hover:bg-surfaceAlt transition-colors"
        v-tooltip="isDark ? 'Cambiar a modo claro' : 'Cambiar a modo oscuro'"
        @click="toggleTheme"
        aria-label="Cambiar tema">
        <Moon
          v-if="!isDark"
          class="w-4 h-4" />
        <Sun
          v-else
          class="w-4 h-4" />
      </button>

      <!-- Notificaciones -->
      <button
        class="relative p-1.5 rounded-lg text-muted hover:text-ink hover:bg-surfaceAlt transition-colors"
        v-tooltip="'Notificaciones'"
        @click="abrirNotificaciones"
        aria-label="Notificaciones">
        <Bell class="w-4 h-4" />
        <span
          v-if="isNewToasts"
          class="absolute top-1 right-1 w-2 h-2 rounded-full bg-danger" />
      </button>

      <!-- User -->
      <div
        v-if="currentUser"
        class="flex items-center gap-2.5 px-3 min-w-0">
        <div class="w-7 h-7 rounded-full bg-brandSoft flex items-center justify-center flex-shrink-0 text-xs font-bold text-brandInk">
          {{ initials }}
        </div>
        <div class="min-w-0 max-w-[120px] sm:max-w-[180px]">
          <p class="text-xs font-semibold text-ink truncate">{{ currentUser.nombreUsuario }}</p>
          <p class="text-[11px] text-muted truncate">{{ role }}</p>
        </div>
      </div>

      <!-- Logout -->
      <button
        class="p-1.5 rounded-lg text-muted hover:text-danger hover:bg-dangerSoft transition-colors"
        v-tooltip="'Cerrar sesión'"
        @click="logout"
        aria-label="Cerrar sesión">
        <LogOut class="w-4 h-4" />
      </button>
    </div>

    <!-- Panel de notificaciones -->
    <AppOffcanvas
      v-model="notifVisible"
      title="Notificaciones"
      width="380px">
      <div
        v-if="toastsHistory.length === 0"
        class="flex flex-col items-center justify-center py-12 text-muted">
        <BellOff class="w-10 h-10 mb-3 opacity-40" />
        <p class="text-sm">Sin notificaciones</p>
      </div>
      <div
        v-else
        class="space-y-2">
        <div
          v-for="(toast, idx) in toastsHistory"
          :key="toast.id ?? idx"
          class="rounded-xl border border-line bg-surfaceAlt p-4">
          <div class="flex items-start justify-between gap-2 mb-1">
            <p class="text-sm font-medium text-ink">{{ toast.titulo }}</p>
            <button
              class="text-muted hover:text-danger transition-colors flex-shrink-0"
              @click="cerrarToast(toast.id)">
              <X class="w-3.5 h-3.5" />
            </button>
          </div>
          <p class="text-xs text-muted">{{ toast.contenido }}</p>
          <p class="text-[11px] text-muted/60 mt-1">
            {{ isMoreThan48Hours(toast.fechaCreacion) ? formattedDate(toast.fechaCreacion, 'DD-MM-YYYY') : formatTimeAgo(toast.fechaCreacion) }}
          </p>
        </div>
      </div>
    </AppOffcanvas>
  </header>
</template>

<script>
import { reactive, toRefs, onMounted, computed } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import Vue3autocounter from 'vue3-autocounter'
import moment from 'moment'
import { Menu as MenuIcon, Moon, Sun, Bell, BellOff, LogOut, X, CircleQuestionMark } from '@lucide/vue'
import { indicadorService, notificacionService } from '@/_services/index.api'
import { bancoCentralService } from '@/_services/bancoCentral.api'
import { formattedDate } from '@/Helper/util'
import { useTheme } from '@/composables/useTheme'
import AppOffcanvas from './ui/AppOffcanvas.vue'

export default {
  name: 'AppHeader',
  components: {
    AppOffcanvas,
    'vue3-autocounter': Vue3autocounter,
    MenuIcon,
    Moon,
    Sun,
    Bell,
    BellOff,
    LogOut,
    X,
    CircleQuestionMark,
  },
  setup() {
    const store = useStore()
    const router = useRouter()
    const { isDark, toggle: toggleTheme } = useTheme()

    const state = reactive({
      notifVisible: false,
      uf: 0,
      dolar: 0,
      euro: 0,
      gbp: 0,
      toastsHistory: computed(() => store.getters.getToastsHistory ?? []),
      isNewToasts: computed(() => store.getters.isNewToasts),
    })

    const indicadoresList = computed(() => [
      { key: 'uf', label: 'UF', value: state.uf },
      { key: 'usd', label: 'USD', value: state.dolar },
      { key: 'eur', label: 'EUR', value: state.euro },
      { key: 'gbp', label: 'GBP', value: state.gbp },
    ])

    const cargarIndicadores = async () => {
      try {
        const res = await indicadorService.getAll()
        if (res.status !== 200) return
        state.uf = res.data.uf?.valor ?? 0
        state.dolar = res.data.dolar?.valor ?? 0
        state.euro = res.data.euro?.valor ?? 0
      } catch (err) {
        // Ignorar error de carga de indicadores
      }
    }

    const cargarGBP = async () => {
      try {
        const res = await bancoCentralService.getValorLibraEsterlina()
        if (res.status !== 200) return
        state.gbp = Number(res.data.Series.Obs[0].value)
      } catch (err) {
        // Ignorar error de carga de GBP
      }
    }

    const logout = () => {
      localStorage.currentUser = null
      router.push({ name: 'Pages', params: { showMsjInvalidToken: 'false' } })
    }

    const abrirNotificaciones = () => {
      state.notifVisible = true
      store.commit('resetToast')
      store.commit('updateIsNewToast', false)
    }

    const cerrarToast = id => {
      if (!id) return
      store.commit('removeToastFromHistory', id)
      notificacionService.setVisibilidad(id).catch(() => {})
    }

    const formatTimeAgo = date => {
      const d = moment(date)
      const mins = moment().diff(d, 'minutes')
      const hrs = moment().diff(d, 'hours')
      const safeMins = Math.max(0, mins)
      if (safeMins < 60) return `Hace ${safeMins} min`
      if (hrs < 24) {
        const hrsQty = Math.floor(safeMins / 60)
        const minsQty = safeMins % 60
        const hrsStr = hrsQty === 1 ? '1 hora' : `${hrsQty} horas`
        if (minsQty === 0) {
          return `Hace ${hrsStr}`
        }
        return `Hace ${hrsStr} ${minsQty} min`
      }
      const days = moment().diff(d, 'days')
      const daysStr = days === 1 ? '1 día' : `${days} días`
      return `Hace ${daysStr}`
    }

    const isMoreThan48Hours = date => moment().diff(moment(date), 'hours') > 48

    const currentUser = computed(() => {
      try {
        return JSON.parse(localStorage.currentUser)
      } catch {
        return null
      }
    })
    const initials = computed(() => {
      if (!currentUser.value?.nombreUsuario) return 'U'
      return currentUser.value.nombreUsuario
        .split(' ')
        .map(n => n[0])
        .slice(0, 2)
        .join('')
        .toUpperCase()
    })

    const role = computed(() => {
      const perfil = currentUser.value?.perfil
      if (Array.isArray(perfil)) return perfil.map(p => p.nombre).join(', ')
      return perfil || ''
    })

    onMounted(() => {
      cargarIndicadores()
      cargarGBP()
    })

    return {
      ...toRefs(state),
      indicadoresList,
      isDark,
      toggleTheme,
      logout,
      abrirNotificaciones,
      cerrarToast,
      formattedDate,
      formatTimeAgo,
      isMoreThan48Hours,
      currentUser,
      initials,
      role,
    }
  },
}
</script>
